import { router } from "@inertiajs/react";
import React, { useContext, useState } from "react";
import { BiSearchAlt } from "react-icons/bi";
import { FiPower } from "react-icons/fi";

import SimpleButton from "@/components/Buttons/SimpleButton";
import { BigNumber } from "@/components/Cards/BigNumber";
import FaqCardComponent from "@/components/Cards/FaqComponent";
import PersonCard from "@/components/Cards/PersonCard";
import CreateConsumerModal from "@/components/Consumers/CreateConsumerModal";
import Dropdown from "@/components/Dropdown";
import Layout from "@/components/Layout";
import ToggleConsumerActiveModal from "@/components/Modal/ToggleConsumerActiveModal";
import RowActionsMenu from "@/components/RowActionsMenu";
import Typography from "@/components/Typography";
import {
  ButtonContainer,
  FiltersContainer,
  HeaderInfo,
  ListSection,
  PageContainer,
} from "@/constants/listIndexStyle";
import { AlertContext } from "@/contexts/Alert";
import { useFilters } from "@/hooks/useFilter";

import { SearchForm } from "../power-plants/style";

const situationOptions: string[] = ["Situação", "Contas em dia", "Inadimplente"];
const activeOptions: string[] = ["Ativos", "Inativos"];

const activeValues = { Ativos: "true", Inativos: "false" };
const situationValues = { Situação: null, "Contas em dia": "paid", Inadimplente: "late" };

const ConsumersPage = ({
  consumersInfo,
  consumers,
  powerPlantsOptions = [
    {
      id: 0,
      name: "Usina",
    },
  ],
  conglomeratesOptions = [
    {
      id: 0,
      name: "Conglomerado",
    },
  ],
}: any) => {
  const {
    data: filters,
    updateFilter,
    transform,
  } = useFilters({
    defaultFilters: {
      situation: "",
      powerPlantName: "",
      conglomerateName: "",
      name: "",
      activeState: "Ativos",
    },
    routerOptions: {
      only: ["consumers", "powerPlantsOptions"],
    },
  });

  transform((data) => ({
    ...data,
    overdue_status:
      situationValues[data?.situation] !== undefined
        ? situationValues[data.situation]
        : data.situation,
    power_plant_id: powerPlantsOptions.find((pw) => pw.name == data.powerPlantName)?.id,
    conglomerate_id: conglomeratesOptions.find((cg) => cg.name == data.conglomerateName)?.id,
    active: activeValues[data?.activeState],
  }));

  const { showAlert } = useContext<any>(AlertContext);
  const [createConsumerModalOpen, setCreateConsumerModalOpen] = useState<boolean>(false);
  const [toggleConsumer, setToggleConsumer] = useState<any>(null);
  const [page, setPage] = useState<number>(1);
  const totalPages = 1;

  const showMore = () => page != totalPages && consumers.length != 0;

  return (
    <Layout>
      <CreateConsumerModal
        isOpen={createConsumerModalOpen}
        onClose={() => setCreateConsumerModalOpen(false)}
        onSaved={(message) => showAlert?.({ message, type: "success" })}
        onError={(message) => showAlert?.({ message, type: "error" })}
      />
      <ToggleConsumerActiveModal
        isOpen={toggleConsumer !== null}
        onClose={() => setToggleConsumer(null)}
        consumer={toggleConsumer}
      />
      <PageContainer>
        <div className="mb-6 flex items-center justify-between gap-4">
          <Typography variant="h3">Clientes</Typography>
          <SimpleButton
            variant="primary"
            className="!w-fit"
            label="Criar cliente"
            onClick={() => setCreateConsumerModalOpen(true)}
          />
        </div>
        {consumersInfo && (
          <HeaderInfo>
            <div className="grid w-full grid-cols-1 justify-between gap-4 pt-5 md:grid-cols-3">
              <BigNumber infoTitle="Número de clientes" info={consumersInfo.consumers_count} />
              <BigNumber
                infoTitle="Número de unidades consumidoras"
                info={consumersInfo.facilities_count}
              />
              <BigNumber infoTitle="inadimplentes" info={consumersInfo.late_consumers_count} />
            </div>
          </HeaderInfo>
        )}

        <ListSection className="flex-col md:flex-row">
          <Typography variant="h4">Listagem</Typography>
          <FiltersContainer className="pt-5 md:pt-0">
            <SearchForm className="search" onSubmit={(e) => e.preventDefault()}>
              <input
                type="text"
                value={filters.name}
                onChange={(e) => updateFilter("name", e.target.value, { debounce: true })}
              />
              <button className="search-icon">
                <BiSearchAlt />
              </button>
            </SearchForm>
            {conglomeratesOptions !== null && (
              <Dropdown
                options={conglomeratesOptions.map((cg) => cg.name)}
                onSelect={(value) => {
                  updateFilter("conglomerateName", value);
                }}
              />
            )}
            {powerPlantsOptions !== null && (
              <Dropdown
                options={powerPlantsOptions.map((pw) => pw.name)}
                onSelect={(value) => {
                  updateFilter("powerPlantName", value);
                }}
              />
            )}
            <Dropdown
              options={situationOptions}
              onSelect={(value) => {
                updateFilter("situation", value);
              }}
            />
            <Dropdown
              options={activeOptions}
              onSelect={(value) => {
                updateFilter("activeState", value);
              }}
            />
          </FiltersContainer>
        </ListSection>

        <HeaderInfo>
          {consumers.length > 0 ? (
            <>
              {consumers.map((consumer) => (
                <PersonCard
                  key={consumer.id}
                  situation={consumer.overdue_status}
                  name={consumer.name}
                  nickname={consumer.nickname}
                  consortia={consumer.consortia_names.join(", ")}
                  facilitiesCount={consumer.facilities_count}
                  active={consumer.active}
                  actions={
                    <RowActionsMenu
                      ariaLabel="Abrir ações do cliente"
                      actions={[
                        {
                          label: "Detalhes",
                          onClick: () => {
                            router.visit(`/manager/internal-consumer/${consumer.user_id}`);
                          },
                        },
                        {
                          label: consumer.active ? "Desativar" : "Ativar",
                          icon: FiPower,
                          className: consumer.active
                            ? "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                            : "flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-green-700 hover:bg-green-50",
                          onClick: () => setToggleConsumer(consumer),
                        },
                      ]}
                    />
                  }
                />
              ))}
            </>
          ) : (
            <Typography variant="h6">Não houve resultado para sua filtragem</Typography>
          )}
        </HeaderInfo>

        {showMore() && (
          <ButtonContainer>
            <SimpleButton
              variant="simple"
              label=" + Carregar mais"
              onClick={() => setPage(page + 1)}
            />
          </ButtonContainer>
        )}

        <div className="pt-36">
          <FaqCardComponent variant="questionSection" />
        </div>
      </PageContainer>
    </Layout>
  );
};
export default ConsumersPage;
